import { useEffect, useRef, useState, type ReactNode } from "react";
import type { ScheduleReadinessRow } from "../lib/api";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { api } from "../lib/api";
import { useTournamentMutationTick } from "../lib/useTournamentMutationTick";
import DivisionChecklistCard from "./DivisionChecklistCard";
import { showGlobalToast } from "./GlobalToasts";
import { checklistBasePath } from "../lib/draftWizard";
import {
  canNavigateToSetupStep,
  setupGate,
  type SetupGateResult,
  type SetupReadinessState,
} from "../lib/setupGates";

export type TournamentSetupStep = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

const SETUP_STEP_COUNT = 10;

// El paso 10 Calendario (Eduardo 2026-08-27): programar canchas es
// OPCIONAL — pueden programarse antes o durante el torneo, y sin calendario
// de canchas no hay nada que programar. Desde Eduardo 2026-10-05 el torneo
// se publica al final de este paso (antes al final del Draw, paso 9).
const STEPS: Array<{ step: TournamentSetupStep; label: string }> = [
  { step: 1, label: "Datos" },
  { step: 2, label: "Reglamento" },
  { step: 3, label: "Convocatoria" },
  // Eduardo 2026-09-23: Comunicación pasa al paso 4 (después de Convocatoria);
  // el cierre "Publicar el torneo" vive al final de Calendario (paso 10).
  { step: 4, label: "Comunicación" },
  { step: 5, label: "Inscripciones" },
  { step: 6, label: "Formato" },
  { step: 7, label: "Ranking" },
  { step: 8, label: "Grupos" },
  { step: 9, label: "Draw" },
  { step: 10, label: "Calendario" },
];

interface Props {
  current: TournamentSetupStep;
  refreshKey?: number;
  /** Estado del torneo: con «draft» los chips del checklist abren su paso. */
  tournamentStatus?: string | null;
  /** «¿Usa calendario de canchas?» para la pastilla de Calendario (por defecto sí). */
  usesCourtCalendar?: boolean;
  /** Encabezado del paso: va entre la barra de pasos y el checklist de categorías. */
  hero?: ReactNode;
}

// Pasos que muestran el «Checklist de categorías» bajo la barra. El Draw (9)
// lo pinta él mismo con su propio aviso de inscritos fuera del draw.
const CHECKLIST_STEPS: ReadonlySet<TournamentSetupStep> = new Set<TournamentSetupStep>([6, 7, 8, 10]);

export default function TournamentSetupProgress({ current, refreshKey = 0, tournamentStatus, usesCourtCalendar = true, hero }: Props) {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const tournamentId = id ? Number(id) : NaN;
  const base = Number.isNaN(tournamentId) ? null : `/admin/tournaments/${tournamentId}`;
  const divisionSuffix = searchParams.get("division") ? `?division=${encodeURIComponent(searchParams.get("division") ?? "")}` : "";

  // Compuertas del wizard (feedback de Eduardo 2026-07-14): los pasos
  // posteriores a una fase incompleta se bloquean con aviso en rojo — ninguna
  // categoría avanza sin formato/ranking/grupos/draw completos.
  const [gate, setGate] = useState<SetupGateResult>({ maxStep: 10, reason: null });
  const [readinessState, setReadinessState] = useState<SetupReadinessState>("loading");
  // Mismas filas del readiness (una sola petición) alimentan el checklist.
  const [readinessRows, setReadinessRows] = useState<ScheduleReadinessRow[]>([]);

  // Se recarga tras cualquier cambio exitoso del torneo (asignar a grupos,
  // generar draw…) sin recargar la página; ver TOURNAMENT_MUTATED_EVENT.
  const mutationTick = useTournamentMutationTick(tournamentId);
  const lastMutationTick = useRef(0);

  useEffect(() => {
    if (!token || Number.isNaN(tournamentId)) return;
    let cancelled = false;
    // Una recarga por cambio no vuelve a «cargando»: los pasos no parpadean.
    const silent = mutationTick !== lastMutationTick.current;
    lastMutationTick.current = mutationTick;
    if (!silent) setReadinessState("loading");
    void api.matches
      .scheduleReadiness(token, tournamentId)
      .then((response) => {
        if (!cancelled) {
          setGate(setupGate(response.data.divisions));
          setReadinessRows(response.data.divisions);
          setReadinessState("loaded");
        }
      })
      .catch(() => {
        if (!cancelled && !silent) setReadinessState("failed");
      });
    return () => {
      cancelled = true;
    };
  }, [token, tournamentId, current, refreshKey, mutationTick]);

  // Wizard reanudable (Eduardo 2026-09-08): al abrir un paso se registra en
  // el torneo; "Abrir torneo" aterriza en el paso más avanzado hasta que el
  // torneo se publica. Best effort: un fallo aquí no bloquea el paso.
  useEffect(() => {
    if (!token || Number.isNaN(tournamentId)) return;
    void api.tournaments.reportSetupStep(token, tournamentId, current).catch(() => undefined);
  }, [token, tournamentId, current]);

  const stepPath = (step: TournamentSetupStep): string | null => {
    if (!base) return null;
    switch (step) {
      case 1: return `${base}/edit${divisionSuffix}`;
      case 2: return `${base}/setup/rules${divisionSuffix}`;
      case 3: return `${base}/setup/call${divisionSuffix}`;
      case 4: return `${base}/setup/settings${divisionSuffix}`;
      case 5: return `${base}/setup/registrations${divisionSuffix}`;
      case 6: return `${base}/setup/format${divisionSuffix}`;
      case 7: return `${base}/setup/ranking${divisionSuffix}`;
      case 8: return `${base}/setup/groups${divisionSuffix}`;
      case 9: return `${base}/setup/rounds${divisionSuffix}`;
      case 10: return `${base}/setup/schedule${divisionSuffix}`;
      default: return null;
    }
  };

  const readinessReason = readinessState === "failed"
    ? "No pudimos verificar que Formato, Grupos y Draw estén completos. Reintenta antes de avanzar."
    : gate.reason;
  const showGateAlert = readinessState === "failed"
    || (gate.reason !== null && current >= gate.maxStep);

  const divisionParam = Number(searchParams.get("division"));
  const selectedDivisionId = Number.isInteger(divisionParam) && divisionParam > 0 ? divisionParam : null;
  const showChecklist = CHECKLIST_STEPS.has(current) && !Number.isNaN(tournamentId);

  return (
    <>
    <section className="setup-progress" aria-label={`Paso ${current} de ${SETUP_STEP_COUNT}`}>
      <div className="setup-progress__heading">
        <span className="setup-progress__step-label">
          Paso {current} de {SETUP_STEP_COUNT}
        </span>
        <span className="setup-progress__step-name">
          {STEPS.find((s) => s.step === current)?.label}
        </span>
      </div>
      <ol className="setup-progress__list setup-progress__list--seven setup-progress__list--eight setup-progress__list--nine setup-progress__list--ten">
        {STEPS.map((entry) => {
          const locked = !canNavigateToSetupStep(entry.step, current, gate, readinessState);
          const state =
            entry.step < current
              ? "is-done"
              : entry.step === current
                ? "is-current"
                : "is-pending";
          const href = stepPath(entry.step);
          const clickable = href !== null && entry.step !== current && !locked;
          return (
            <li
              key={entry.step}
              className={`setup-progress__item ${state}${clickable ? " is-clickable" : ""}${locked ? " is-locked" : ""}`}
              aria-current={entry.step === current ? "step" : undefined}
              title={locked ? readinessReason ?? undefined : undefined}
            >
              {clickable ? (
                <Link to={href} className="setup-progress__link">
                  <span className="setup-progress__bullet">{entry.step}</span>
                  <span className="setup-progress__label">{entry.label}</span>
                </Link>
              ) : locked ? (
                // Paso bloqueado: el motivo se dice cuando el admin INTENTA entrar.
                <button
                  type="button"
                  className="setup-progress__link setup-progress__link--locked"
                  aria-disabled="true"
                  onClick={() => showGlobalToast(readinessReason ?? "Completa el paso actual antes de avanzar.", "error")}
                >
                  <span className="setup-progress__bullet">🔒</span>
                  <span className="setup-progress__label">{entry.label}</span>
                </button>
              ) : (
                <>
                  <span className="setup-progress__bullet">{entry.step}</span>
                  <span className="setup-progress__label">{entry.label}</span>
                </>
              )}
            </li>
          );
        })}
      </ol>
      {showGateAlert ? (
        <p className="form-error setup-progress__gate-alert" role="status" data-no-toast>
          {readinessReason}
        </p>
      ) : null}
    </section>
    {hero ?? null}
    {showChecklist ? (
      <DivisionChecklistCard
        tournamentId={tournamentId}
        rows={readinessRows}
        state={readinessState}
        checklistBase={checklistBasePath(tournamentId, true, tournamentStatus)}
        lockScheduleChip={tournamentStatus === "draft"}
        usesCourtCalendar={usesCourtCalendar}
        selectedDivisionId={selectedDivisionId}
      />
    ) : null}
    </>
  );
}
